import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { useAuth } from "../lib/auth";
import TrialRenewalBanner from "../components/TrialRenewalBanner";
import OnboardingChecklist from "../components/OnboardingChecklist";
import HelpAndSupportCard from "../components/HelpAndSupportCard";
import {
  Users,
  ReceiptText,
  AlertTriangle,
  Wallet,
  TrendingUp,
  ShieldCheck,
  ShieldAlert,
  Ban,
  UserX,
  ChevronRight,
  Phone,
  Hourglass,
  XCircle,
  Zap,
  Gauge,
  FileText,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

function StatCard({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition"
    >
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pendingSubs, setPendingSubs] = useState([]);
  const [latestRejected, setLatestRejected] = useState(null);
  const [usage, setUsage] = useState(null);
  const [custStats, setCustStats] = useState(null);
  const [agrStats, setAgrStats] = useState(null);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data));
    api.get("/payments/submissions").then((r) => {
      const subs = r.data || [];
      setPendingSubs(subs.filter((s) => s.status === "pending"));
      const rej = subs.find((s) => s.status === "rejected");
      setLatestRejected(rej || null);
    }).catch(() => {});
    api.get("/usage/me").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/customers-stats").then((r) => setCustStats(r.data)).catch(() => {});
    api.get("/agreements/stats").then((r) => setAgrStats(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {["a","b","c","d","e","f"].map((slot) => (
            <div key={slot} className="h-32 bg-white border border-slate-200 rounded-md animate-pulse" />
          ))}
        </div>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Dashboard</h1>
        <p className="text-slate-500 mt-1">Aap ki shop ka poora hisaab — ek nazar mein.</p>
      </div>

      <OnboardingChecklist />

      {pendingSubs.length > 0 && (
        <Link to="/subscription" className="block bg-amber-50 border border-amber-200 rounded-md p-4 hover:bg-amber-100 transition" data-testid="dash-pending-payment-banner">
          <div className="flex items-center gap-3">
            <Hourglass className="w-5 h-5 text-amber-700 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-800">Payment Pending Approval</div>
              <div className="text-amber-700">Aap ki recent payment admin verify kar rahe hain. Approval ke baad plan turant active ho jaye ga.</div>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-700" />
          </div>
        </Link>
      )}

      {/* Trial renewal: show only when no pending submission; otherwise the
          pending banner is the stronger signal. Still show after expiry. */}
      {pendingSubs.length === 0 && (
        <TrialRenewalBanner usage={usage} shopName={user?.shop_name} />
      )}

      {pendingSubs.length === 0 && latestRejected && (
        <Link to="/subscription" className="block bg-red-50 border border-red-200 rounded-md p-4 hover:bg-red-100 transition" data-testid="dash-rejected-payment-banner">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-700 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-red-800">Last payment rejected</div>
              <div className="text-red-700">Reason: {latestRejected.reject_reason || "—"}. Resubmit karein.</div>
            </div>
            <ChevronRight className="w-4 h-4 text-red-700" />
          </div>
        </Link>
      )}

      <UsageCard usage={usage} />

      {/* 6 KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={Users} label="Total Customers" value={data.total_customers} tone="slate" testid="card-customers" />
        <StatCard icon={ReceiptText} label="Active Loans" value={data.active_loans} tone="sky" testid="card-active-loans" />
        <StatCard icon={Wallet} label="Total Outstanding" value={fmtPKR(data.outstanding)} tone="slate" testid="card-outstanding" />
        <StatCard icon={TrendingUp} label="Today's Collection" value={fmtPKR(data.today_collection)} tone="emerald" testid="card-today-collection" />
        <StatCard
          icon={AlertTriangle}
          label="Overdue Amount"
          value={fmtPKR(data.overdue_amount)}
          hint={`${data.overdue_count} loan(s)`}
          tone="red"
          testid="card-overdue"
        />
        <StatCard
          icon={ShieldCheck}
          label="Recovery Rate"
          value={`${data.recovery_rate}%`}
          hint="Paid ÷ due-to-date"
          tone={data.recovery_rate >= 85 ? "emerald" : data.recovery_rate >= 60 ? "amber" : "red"}
          testid="card-recovery-rate"
        />
      </div>

      {/* Agreement stats */}
      {agrStats && (
        <div className="space-y-2" data-testid="agreement-stats">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500">Agreements</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <StatCard icon={FileText} label="Total Agreements" value={agrStats.total} tone="slate" testid="agr-total" />
            <StatCard icon={Hourglass} label="Pending Signing" value={agrStats.pending} tone="amber" testid="agr-pending" />
            <StatCard icon={ShieldCheck} label="Signed" value={agrStats.signed} tone="emerald" testid="agr-signed" />
          </div>
        </div>
      )}

      {/* Customer Quality stats */}
      {custStats && (
        <div className="space-y-2" data-testid="customer-quality-stats">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-500" />
            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500">Customer Quality</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
            <StatCard icon={ShieldCheck} label="Verified" value={custStats.verified_customers} tone="emerald" testid="cq-verified" />
            <StatCard icon={ShieldAlert} label="High Risk" value={custStats.high_risk_customers} tone="red" testid="cq-high-risk" />
            <StatCard icon={Ban} label="Blacklisted" value={custStats.blacklisted_customers} tone="red" testid="cq-blacklisted" />
            <StatCard icon={UserX} label="No Guarantor" value={custStats.customers_without_guarantor} hint="Risk: high" tone="amber" testid="cq-no-guarantor" />
            <StatCard icon={AlertTriangle} label="Active Defaulters" value={custStats.active_defaulters} tone="amber" testid="cq-defaulters" />
            <StatCard icon={Gauge} label="Avg Risk Score" value={`${custStats.average_risk_score}/100`} tone={custStats.average_risk_score > 60 ? "red" : custStats.average_risk_score > 30 ? "amber" : "emerald"} testid="cq-avg-risk" />
            <StatCard
              icon={TrendingUp}
              label="Recovery Success"
              value={custStats.recovery_success_rate != null ? `${custStats.recovery_success_rate}%` : "—"}
              hint={custStats.recovery_success_rate != null ? "closed ÷ (closed+default)" : "no closed/defaulted loans yet"}
              tone="sky"
              testid="cq-recovery-success"
            />
            <StatCard icon={Users} label="Total Customers" value={custStats.total_customers} tone="slate" testid="cq-total" />
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-display font-semibold text-lg">Last 7 days</div>
              <div className="text-xs text-slate-500">Daily collections.</div>
            </div>
          </div>
          <div className="h-64 mt-4" data-testid="collections-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.collections_chart}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip formatter={(v) => fmtPKR(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0" }} />
                <Bar dataKey="amount" fill="#059669" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-display font-semibold text-lg">Monthly trend · 6 months</div>
              <div className="text-xs text-slate-500">Collections by month.</div>
            </div>
          </div>
          <div className="h-64 mt-4" data-testid="monthly-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_trend}>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip formatter={(v) => fmtPKR(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0" }} />
                <Line type="monotone" dataKey="amount" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: "#059669" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top defaulters */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="font-display font-semibold text-lg">Top defaulters</div>
            <div className="text-xs text-slate-500">Act on these today.</div>
          </div>
          <Link to="/recovery" className="text-sm font-semibold text-emerald-700 flex items-center gap-1" data-testid="to-recovery-link">
            View all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {(!data.top_defaulters || data.top_defaulters.length === 0) ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="no-defaulters">All clear — no defaulters right now.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.top_defaulters.map((d, i) => (
              <Link
                to={`/customers/${d.customer_id}`}
                key={d.loan_id}
                className="flex items-center gap-4 p-4 hover:bg-slate-50"
                data-testid={`top-defaulter-${d.loan_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-red-50 border border-red-200 text-red-700 flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{d.customer_name}</div>
                  <div className="text-xs text-slate-500 tabular flex items-center gap-2">
                    <Phone className="w-3 h-3" /> {d.customer_mobile} · {d.product_name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-display font-bold text-red-600 tabular">{d.days_late}d late</div>
                  <div className="text-xs tabular text-slate-600">{fmtPKR(d.remaining_amount)}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function UsageCard({ usage }) {
  if (!usage) return null;
  const rows = [
    { key: "customers", label: "Customers", used: usage.customers_used, limit: usage.max_customers },
    { key: "loans", label: "Active loans", used: usage.active_loans_used, limit: usage.max_active_loans },
  ];
  const isTrial = usage.plan_id === "trial";
  const isFounding = !!usage.is_founding_member;
  const nearLimit = rows.some((r) => r.limit != null && r.used >= Math.max(1, Math.floor(r.limit * 0.8)));
  const tone = isTrial && nearLimit ? "amber" : "slate";
  return (
    <div data-testid="dash-usage-card" className={`rounded-md border p-4 sm:p-5 bg-white ${tone === "amber" ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 flex-wrap">
            <Zap className="w-3 h-3 text-emerald-600" /> Plan · <span className="text-slate-700 capitalize">{usage.plan_id}</span>
            {isFounding && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-2 py-0.5 text-[10px] font-bold uppercase" data-testid="dash-founding-badge">
                🏆 Founding #{usage.founder_no}
              </span>
            )}
          </div>
          <div className="font-display text-lg font-bold mt-0.5">Your usage</div>
          {usage.trial_ends && (
            <div className="text-xs text-slate-500 mt-0.5 tabular">
              Expires {String(usage.trial_ends).slice(0, 10)} · {usage.days_left} day{usage.days_left === 1 ? "" : "s"} left
            </div>
          )}
        </div>
        {isTrial && (
          <Link to="/subscription" data-testid="dash-usage-upgrade" className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 transition">
            Upgrade plan <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        {rows.map((r) => {
          const unlimited = r.limit == null;
          const over = !unlimited && r.used >= r.limit;
          const pct = unlimited ? 0 : Math.min(100, Math.round((r.used / Math.max(1, r.limit)) * 100));
          const bar = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div key={r.key} data-testid={`dash-usage-${r.key}`} className={`border rounded-md p-3 ${over ? "border-red-300 bg-red-50" : "border-slate-100 bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-600">{r.label}</div>
                <div className="text-xs tabular text-slate-600">
                  {unlimited ? (
                    <span className="font-bold text-emerald-700">Unlimited</span>
                  ) : (
                    <span><span className={`font-bold ${over ? "text-red-700" : "text-slate-900"}`}>{r.used}</span> / {r.limit}</span>
                  )}
                </div>
              </div>
              {!unlimited && (
                <div className="mt-2 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <HelpAndSupportCard />
    </div>
  );
}
